import { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { HYDRA_RET, HYDRA_VIP_DASHBOARD } from 'constants/hydraTests';
import { evOnShowRewardsTransparencyClick } from 'events/global';

import VipOnlyLogo from 'components/icons/vipDashboard/VipOnlyLogo';
import MartyLink from 'components/common/MartyLink';
import ProductUtils from 'helpers/ProductUtils';
import { toUSD } from 'helpers/NumberFormats';
import { track } from 'apis/amethyst';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { isAssigned, triggerAssignment } from 'actions/ab';
import stars from 'images/stars.svg';
import css from 'styles/components/common/rewardsTransparency.scss';

export class RewardsTransparency extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    marketplace: PropTypes.object
  }

  componentDidMount() {
    const { triggerAssignment } = this.props;
    triggerAssignment(HYDRA_RET);
    triggerAssignment(HYDRA_VIP_DASHBOARD);
  }

  handleCtaClick = () => {
    const { isForCartModal, pageType, isRecognizedCustomer, pointsForCurrentPage, rewards } = this.props;
    track(evOnShowRewardsTransparencyClick, { isForCartModal, pageType, rewards, isRecognizedCustomer, pointsForCurrentPage });
  }

  makeRecognizedStatusText = () => {
    const { handleCtaClick } = this;
    const { isRecognizedCustomer, rewards: { isEnrolled } } = this.props;

    if (isRecognizedCustomer && isEnrolled) {
      return '';
    }

    if (isRecognizedCustomer && !isEnrolled) {
      return <>Not a Rewards member? <Link onClick={handleCtaClick} to="/rewards/dashboard">Join today to start earning!</Link></>;
    }

    return <Link onClick={handleCtaClick} to="/rewards/dashboard">Sign in to get started!</Link>;
  }

  makeText = () => {
    const { makeRecognizedStatusText } = this;
    const {
      isForCartModal, pageType, pointsForCurrentPage,
      isVIPDashboard,
      rewards: { transparencyPointsForCart }
    } = this.props;

    const cartPoints = transparencyPointsForCart?.totalPoints?.dollarValue;
    if (isForCartModal) {
      return !!cartPoints && (
        <p>
          {
            isVIPDashboard
              ? <>Check out to earn Zappos VIP points worth up to <span>{toUSD(cartPoints, false)}</span> in VIP codes.</>
              : <>Check out to earn Zappos Rewards points worth up to <span>{toUSD(cartPoints, false)}</span> in Rewards codes.</>
          }
        </p>
      );
    }

    if (pageType === 'cart' && pointsForCurrentPage) {
      return (
        <p>
          {
            isVIPDashboard
              ? <>Check out to earn Zappos VIP points worth up to <span>{pointsForCurrentPage}</span> in VIP codes. {makeRecognizedStatusText()}</>
              : <>Check out to earn Zappos Rewards points worth up to <span>{pointsForCurrentPage}</span> in Rewards codes. {makeRecognizedStatusText()}</>
          }
        </p>
      );
    }

    if (pageType === 'product' && pointsForCurrentPage) {
      return (
        <p>
          {
            isVIPDashboard
              ? <>Earn Zappos VIP points worth up to <span>{pointsForCurrentPage}</span> in VIP codes on this item. {makeRecognizedStatusText()}</>
              : <>Earn Zappos Rewards points worth up to <span>{pointsForCurrentPage}</span> in Rewards codes on this item. {makeRecognizedStatusText()}</>
          }
        </p>
      );
    }

    return null;

  }

  render() {
    const { makeText } = this;
    const {
      isAssignedInRewardsTransparency,
      isForCartModal,
      isVIPDashboard,
      pageType,
      isGiftCard,
      accountRewards = {}
    } = this.props;
    const { testId, marketplace: { hasRewardsTransparency } } = this.context;

    if (!hasRewardsTransparency || !isAssignedInRewardsTransparency || (pageType === 'product' && isGiftCard && !isForCartModal)) {
      return null;
    }

    if (isVIPDashboard && pageType === 'account') {
      const { rewardsInfo } = accountRewards;
      return rewardsInfo && !rewardsInfo.consented && rewardsInfo.canEnroll && <div
        className={cn(css.page, { [css.account]: pageType === 'account' && !isForCartModal })}
        data-test-id={testId('rewardsTransparencyContainer')}>
        <VipOnlyLogo />
        <MartyLink data-test-id={testId('viewDashboard')} to="/rewards/dashboard">Become a Zappos VIP for <span>FREE</span>!</MartyLink> VIP members get Free Next Business Day Shipping on every order and more!
      </div>;
    }

    const rewardsText = makeText();

    return !!rewardsText && (
      <div
        className={cn(css.page, {
          [css.cart]: pageType === 'cart' && !isForCartModal,
          [css.pdp]: pageType === 'product' && !isForCartModal,
          [css.brand]: pageType === 'brand' && !isForCartModal,
          [css.checkout]: pageType === 'checkout' && !isForCartModal,
          [css.cartModal]: !!isForCartModal
        })}
        data-test-id={testId('rewardsTransparencyContainer')}>
        <img src={stars} alt="stars" />
        {rewardsText}
      </div>
    );
  }
}

const pointsToPageMap = {
  'cart': 'transparencyPointsForCart',
  'checkout': 'transparencyPointsForCart',
  'homepage': 'transparencyPointsForBrand',
  'landing': 'transparencyPointsForBrand',
  'product': 'transparencyPointsForItem'
};

const mapStateToProps = state => {
  const { product, rewards: accountRewards, sharedRewards: rewards, cookies: { 'x-main': xMain }, pageView: { pageType } } = state;
  const isAssignedInRewardsTransparency = isAssigned(HYDRA_RET, 1, state);
  const isRecognizedCustomer = !!xMain;
  const pointsForCurrentPage = toUSD(rewards[pointsToPageMap[pageType]]?.totalPoints?.dollarValue, false);

  return {
    accountRewards,
    rewards,
    pageType,
    pointsForCurrentPage,
    isAssignedInRewardsTransparency,
    isRecognizedCustomer,
    isGiftCard: ProductUtils.isGiftCard(product?.detail?.defaultProductType),
    isVIPDashboard: isAssigned(HYDRA_VIP_DASHBOARD, 1, state)
  };
};

RewardsTransparency.displayName = 'RewardsTransparency';

export default withErrorBoundary('RewardsTransparency', connect(mapStateToProps, {
  triggerAssignment
})(RewardsTransparency));
